import { Link } from 'react-router'
import { cn } from '@/lib/cn'

/*
 * `.animated-button` — the gradient-blob CTA used by every BottomCTA.
 *
 * Four blurred colour blobs rotate behind a dark pill, doubled once more
 * through a blur layer. Webflow drove this with an interactions timeline that
 * set transform and opacity per blob; here it is four CSS animations, which is
 * both smaller and honest about being decorative.
 *
 * The blobs are aria-hidden and the whole animation is disabled under
 * prefers-reduced-motion — the Webflow original respected neither.
 */

const BLOBS = [
  { className: '-top-[24%] -left-[11%] bg-(image:--gradient-cyan-blue)', delay: '0s' },
  { className: '-top-[15%] right-[54%] bg-(image:--gradient-brand)', delay: '-2s' },
  { className: 'top-[52%] -right-[27%] bg-(image:--gradient-brand)', delay: '-4s' },
  { className: 'top-[53%] right-[5%] bg-(image:--gradient-cyan-blue)', delay: '-6s' },
]

function Blobs({ blur = false }: { blur?: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'pointer-events-none absolute inset-0 z-[var(--z-raised)] h-full w-full overflow-hidden rounded-pill',
        blur && 'blur-[5px]',
      )}
    >
      {BLOBS.map((b) => (
        <span
          key={b.className}
          style={{ animationDelay: b.delay }}
          className={cn(
            'absolute z-[var(--z-base)] h-[50px] w-[50px] rounded-full blur-[16px] motion-safe:animate-[singulate-orbit_8s_linear_infinite]',
            b.className,
          )}
        />
      ))}
    </span>
  )
}

export function AnimatedButton({
  href,
  children,
  className,
}: {
  href: string
  children: React.ReactNode
  className?: string
}) {
  const inner = (
    <>
      <Blobs blur />
      <Blobs />
      <span className="relative z-[var(--z-raised)] flex items-center justify-center overflow-hidden rounded-pill bg-neutral-900 px-8 py-3 transition-colors duration-200 group-hover:bg-grayscale-900/95">
        <span className="relative font-sans text-small font-medium text-white">{children}</span>
      </span>
    </>
  )
  const classes = cn(
    'group relative z-[var(--z-base)] inline-flex cursor-pointer items-center justify-center rounded-pill p-px no-underline transition-all duration-200 hover:shadow-[0_0_25px_--alpha(var(--color-blue-500)/30%)] focus-ring',
    className,
  )

  return /^https?:|^#/.test(href) ? (
    <a href={href} className={classes}>
      {inner}
    </a>
  ) : (
    <Link to={href} className={classes}>
      {inner}
    </Link>
  )
}
